import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
  structuredData?: object;
  preloadImage?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  ogImage,
  canonicalUrl,
  noIndex = false,
  structuredData,
  preloadImage
}) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  
  const baseUrl = 'https://mappa-caeli.com';
  const currentUrl = `${baseUrl}${location.pathname}`;
  
  // Default SEO values based on route
  const getDefaultSEO = () => {
    const route = location.pathname;
    
    const seoMap: Record<string, { title: string; description: string; keywords: string }> = {
      '/': {
        title: 'Mappa Caeli - Traditional Catholic Catechism for Children | Saint Pius X',
        description: 'Interactive Catholic education platform teaching children traditional faith through the Catechism of Saint Pius X. Engaging quests, activities, and family-friendly religious education.',
        keywords: 'Catholic catechism, Saint Pius X, traditional Catholic education, children religious education, Catholic homeschool, family faith formation, Catholic curriculum, religious education games'
      },
      '/saint-pius-x': {
        title: 'Saint Pius X - Pope of Traditional Catechism | Mappa Caeli',
        description: 'Learn about Saint Pius X, the Pope who championed traditional Catholic catechism and religious education for children. Biography, timeline, and prayers.',
        keywords: 'Saint Pius X, Pope Pius X, traditional catechism, Catholic saints, papal history, first communion, religious education reform, Catholic pope'
      },
      '/catechism': {
        title: 'Traditional Catholic Catechism Program | Mappa Caeli',
        description: 'Comprehensive Catholic catechism program based on Saint Pius X teachings. Interactive lessons, quizzes, and activities for children and families.',
        keywords: 'Catholic catechism program, traditional Catholic education, religious curriculum, catechism lessons, Catholic doctrine, faith formation, children catechism'
      },
      '/quest-map': {
        title: 'Interactive Catholic Learning Quest Map | Mappa Caeli',
        description: 'Explore our interactive quest map with Catholic lessons, activities, and adventures. Gamified religious education for children.',
        keywords: 'Catholic learning games, religious education quests, interactive Catholic lessons, children faith activities, gamified catechism, Catholic adventure learning'
      },
      '/blog': {
        title: 'Catholic Family Education Blog | Mappa Caeli',
        description: 'Catholic parenting tips, homeschool resources, and traditional faith formation articles for families seeking authentic Catholic education.',
        keywords: 'Catholic parenting blog, Catholic homeschool, traditional Catholic family, religious education articles, Catholic family life, faith formation resources'
      },
      '/about': {
        title: 'About Mappa Caeli - Catholic Family Education Mission',
        description: 'Meet the Pessoa family behind Mappa Caeli. Our mission to provide traditional Catholic education and strengthen family faith formation.',
        keywords: 'Catholic education founders, Pessoa family, traditional Catholic homeschool, Catholic family mission, religious education platform, Catholic entrepreneurs'
      },
      '/subscription': {
        title: 'Catholic Education Subscription Plans | Mappa Caeli',
        description: 'Affordable Catholic education subscription plans for families. Full access to traditional catechism lessons, quizzes, and activities.',
        keywords: 'Catholic education subscription, affordable Catholic curriculum, family education plans, Catholic homeschool subscription, religious education pricing'
      },
      '/donate': {
        title: 'Support Catholic Education - Donate to Mappa Caeli',
        description: 'Help us become a full-time Catholic developer! Your donation supports free Catholic educational apps for families worldwide.',
        keywords: 'Catholic donation, support Catholic education, donate Catholic app, Catholic developer, religious education funding, Catholic nonprofit'
      }
    };
    
    return seoMap[route] || seoMap['/'];
  };
  
  const defaultSEO = getDefaultSEO();
  const finalTitle = title || defaultSEO.title;
  const finalDescription = description || defaultSEO.description;
  const finalKeywords = keywords || defaultSEO.keywords;
  const finalCanonicalUrl = canonicalUrl || currentUrl;
  const finalOgImage = ogImage || `${baseUrl}/assets/mappa-caeli-logo.png`;

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <link rel="canonical" href={finalCanonicalUrl} />
      <meta name="language" content={i18n.language} />
      
      {/* Preload critical images for LCP optimization */}
      {preloadImage && (
        <link 
          rel="preload" 
          as="image" 
          href={preloadImage}
          fetchPriority="high"
        />
      )}
      
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:url" content={finalCanonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={i18n.language === 'en' ? 'en_US' : i18n.language === 'fr' ? 'fr_FR' : 'pt_BR'} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalOgImage} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;